/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { LinkButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import AiModelIcon from '@atlaskit/icon-lab/core/ai-model';
import Image from '@atlaskit/image';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';
import { type ActivityItem, useActivityClient } from '@atlassian/recent-work-client';
import { RecentActivityItemProvider, RecentWorkSkeletonPanel } from '@atlassian/recent-work-ui';

import { messages } from './messages';
import RelativeDateLabel from './RelativeDateLabel';

export interface WorkProfileScreenProps {
	seeAllActivityHref?: string;
}

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	header: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		paddingBlock: token('space.250'),
		flexShrink: 0,
	},
	iconBadge: {
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.full'),
		color: token('color.text.inverse'),
		width: '32px',
		height: '32px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	titleText: {
		font: token('font.heading.medium'),
		color: token('color.text'),
	},
	divider: {
		height: '1px',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		flexShrink: 0,
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
	},
	sectionHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		flexShrink: 0,
		paddingBlock: token('space.200'),
	},
	sectionHeadingText: {
		font: token('font.heading.xsmall'),
		color: token('color.text'),
	},
	capitalize: {
		textTransform: 'capitalize',
	},
	activityRow: {
		display: 'block',
		boxSizing: 'border-box',
		width: '100%',
		borderRadius: token('radius.small'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		color: token('color.text'),
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			textDecoration: 'none',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	// Keep the relative date on a single line and prevent it from shrinking.
	activityDate: {
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
});

export const WorkProfileScreen = ({ seeAllActivityHref }: WorkProfileScreenProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const activityClient = useActivityClient('v3', 'rovo-settings-modal');
	const [recentActivities, setRecentActivities] = useState<ActivityItem[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		setIsLoading(true);
		activityClient
			.fetchActivities(
				[50, 125, 250], // retries
				{
					limit: 7,
				},
				['workedOn'],
			)
			.then((fetchedActivities) => {
				setRecentActivities(fetchedActivities.workedOn);
			})
			.finally(() => setIsLoading(false));
	}, [activityClient]);

	return (
		<Box xcss={styles.root}>
			<Box xcss={styles.header}>
				<Inline space="space.150" alignBlock="center">
					<Box xcss={styles.iconBadge}>
						<AiModelIcon label="" size="medium" />
					</Box>
					<Stack space="space.025">
						<Box xcss={styles.titleText}>{formatMessage(messages.title)}</Box>
						<Text size="small" color="color.text.subtle">
							{formatMessage(messages.subtitle)}
						</Text>
					</Stack>
				</Inline>
			</Box>
			<Box xcss={styles.divider} />
			<Box xcss={styles.body}>
				<Box xcss={styles.sectionHeader}>
					<Box xcss={styles.sectionHeadingText}>
						{formatMessage(messages.recentActivityHeading)}
					</Box>

					{seeAllActivityHref && (
						<LinkButton appearance="default" href={seeAllActivityHref} target="_blank">
							{formatMessage(messages.seeAllActivity)}
						</LinkButton>
					)}
				</Box>

				<Stack space="space.200">
					{isLoading ? (
						<RecentWorkSkeletonPanel />
					) : (
						recentActivities.map((item) => (
							<RecentActivityItemProvider key={item.object.id} item={item}>
								<Anchor
									href={item.object.url}
									target="_blank"
									xcss={styles.activityRow}
									interactionName="rovo-settings-work-activity-item"
								>
									<Inline spread="space-between">
										<Inline space="space.150" alignBlock="center">
											<Tile label="">
												<Image src={item.object.icon?.value} alt="" width={24} height={24} />
											</Tile>
											<Stack space="space.025">
												<Text maxLines={1} as="p" color="color.text" weight="medium">
													{item.object.name}
												</Text>
												<Inline separator="•" space="space.075" alignBlock="center">
													<Inline space="space.075">
														<Text color="color.text.subtle" size="small" maxLines={1}>
															<span css={styles.capitalize}>
																{item.object.product.split('-').join(' ')}
															</span>
														</Text>
														<Text color="color.text.subtle" size="small" maxLines={1}>
															<span css={styles.capitalize}>{item.object.type}</span>
														</Text>
													</Inline>
													{item.containers
														.map((container) => container.name)
														.filter(Boolean)
														.map((name: string) => (
															<Text key={name} color="color.text.subtle" size="small" maxLines={1}>
																{name}
															</Text>
														))}
												</Inline>
											</Stack>
										</Inline>
										<Box xcss={styles.activityDate}>
											<Text color="color.text.subtle">
												<RelativeDateLabel timestamp={item.timestamp} />
											</Text>
										</Box>
									</Inline>
								</Anchor>
							</RecentActivityItemProvider>
						))
					)}
				</Stack>
			</Box>
		</Box>
	);
};

export default WorkProfileScreen;
